import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  Loader2, LogOut, ShieldCheck, Clock, ArrowLeft, MapPin, User as UserIcon, Phone,
  Camera, Play, Square, Wrench, Navigation2, CheckCircle2, ImageIcon, Delete, X,
  History
} from "lucide-react";
import useFieldEvents from "../hooks/useFieldEvents";
import usePwaManifest from "../hooks/usePwaManifest";
import useStandaloneLinks from "../hooks/useStandaloneLinks";
import ErrorBoundary from "../admin/ErrorBoundary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELD_TOKEN_KEY = "gow_field_token";

const toastOpts = { style: { background: "#1A4D2E", color: "#fff", border: "1px solid #2E8B57" } };

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }); }
  catch { return iso; }
}
function fmtDateLabel(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }); }
  catch { return iso; }
}
function elapsed(start) {
  if (!start) return "0:00";
  const ms = Date.now() - new Date(start).getTime();
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}
function elapsedHMS(start) {
  if (!start) return "00:00:00";
  const ms = Date.now() - new Date(start).getTime();
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function FieldAppInner() {
  usePwaManifest({ manifest: "/api/pwa/manifest/field.json", title: "GOW Field" });
  useStandaloneLinks();
  const [token, setToken] = useState(() => sessionStorage.getItem(FIELD_TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [openShift, setOpenShift] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [view, setView] = useState("today"); // "today" | "history"
  const [pastTickets, setPastTickets] = useState({ groups: [] });
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  // Register service worker for PWA installability (scoped to /field)
  useEffect(() => {
    if ("serviceWorker" in navigator && process.env.NODE_ENV !== "test") {
      navigator.serviceWorker
        .register("/sw.js", { scope: "/field" })
        .catch((err) => console.warn("[field PWA] SW registration failed:", err?.message || err));
    }
  }, []);

  // Hide host iOS splash element if it exists
  useEffect(() => {
    const el = document.getElementById("splash-screen");
    if (el) { el.style.opacity = "0"; el.style.display = "none"; }
  }, []);

  // Tick every second so elapsed timers refresh
  useEffect(() => {
    if (!openShift && !selectedTicketId) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [openShift, selectedTicketId]);

  const fetchMe = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/field/me`, { headers: authHeaders });
      setUser(data.user);
      setOpenShift(data.open_shift);
    } catch (e) {
      sessionStorage.removeItem(FIELD_TOKEN_KEY);
      setToken(null);
      if (e.response?.status === 401) toast.error("Session expired. Sign in again.");
    } finally { setLoading(false); }
  }, [token, authHeaders]);

  // Compute today's date in the device's local timezone — robust against UTC date
  // rollover (Eastern Time crosses UTC midnight at 7-8 PM local).
  const localToday = useCallback(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);

  const fetchTickets = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/field/tickets`, {
        headers: authHeaders,
        params: { date: localToday() },
      });
      setTickets(data);
    } catch (e) { console.warn("[field] tickets failed:", e?.message); }
  }, [token, authHeaders, localToday]);

  useEffect(() => { if (token) { fetchMe(); fetchTickets(); } }, [token, fetchMe, fetchTickets]);

  const fetchHistory = useCallback(async () => {
    if (!token) return;
    setLoadingHistory(true);
    try {
      const { data } = await axios.get(`${API}/field/tickets/history?days=14`, { headers: authHeaders });
      setPastTickets(data || { groups: [] });
    } catch (e) {
      console.warn("[field] history failed:", e?.message);
      setPastTickets({ groups: [] });
    } finally {
      setLoadingHistory(false);
    }
  }, [token, authHeaders]);

  // Auto-load history the first time the user opens the History view
  useEffect(() => {
    if (view === "history" && token) fetchHistory();
  }, [view, token, fetchHistory]);

  // Refetch when the tab regains focus (worker comes back to the app) and at local midnight
  useEffect(() => {
    if (!token) return;
    const onFocus = () => fetchTickets();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") fetchTickets();
    });
    // Schedule a refetch right after local midnight to roll the date over cleanly
    const now = new Date();
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 30);
    const ms = tomorrow.getTime() - now.getTime();
    const t = setTimeout(() => fetchTickets(), ms);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearTimeout(t);
    };
  }, [token, fetchTickets]);

  // Real-time updates from admin/server (drag-drop reorders, new assignments, etc.)
  const [liveSignal, setLiveSignal] = useState(0); // bump to force ticket-detail refetch
  useFieldEvents(useCallback((ev) => {
    // Always re-pull the ticket list — cheap and keeps cards/route order in sync
    fetchTickets();
    // Bump signal so the open ticket detail (if any) re-fetches
    setLiveSignal((s) => s + 1);
    // Friendly toast based on event type
    if (ev.type === "so_assigned") toast("New job assigned to you", { description: ev.scheduled_date || "" });
    else if (ev.type === "so_reordered") toast("Route order updated", { description: "Your stops were reordered" });
    else if (ev.type === "so_updated") toast("Job details updated", { description: "Manager edited a ticket" });
    else if (ev.type === "so_facade_updated") toast(ev.removed ? "Facade photo removed" : "Facade photo updated");
  }, [fetchTickets]), { enabled: !!token });

  const handlePinDigit = (d) => {
    if (pin.length >= 4) return;
    setPin(p => (p + d).slice(0, 4));
  };
  const handlePinDel = () => setPin(p => p.slice(0, -1));
  const handlePinClear = () => setPin("");

  const handleLogin = useCallback(async () => {
    if (pin.length !== 4) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/field/login`, { pin });
      sessionStorage.setItem(FIELD_TOKEN_KEY, data.token);
      setToken(data.token);
      setUser(data.user);
      toast.success(`Welcome, ${data.user.name}!`);
      setPin("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid PIN");
      setPin("");
    } finally { setBusy(false); }
  }, [pin]);

  useEffect(() => {
    if (pin.length === 4 && !token) handleLogin();
  }, [pin, token, handleLogin]);

  const handleClockIn = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/field/clock-in`, {}, { headers: authHeaders });
      toast.success("Clocked in. Have a great day!");
      await fetchMe();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const handleClockOut = async () => {
    if (!window.confirm("End your shift now?")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/field/clock-out`, {}, { headers: authHeaders });
      toast.success("Clocked out. Great work today!");
      await fetchMe();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(FIELD_TOKEN_KEY);
    setToken(null); setUser(null); setOpenShift(null); setTickets([]); setSelectedTicketId(null);
  };

  // ====================== Login screen ======================
  if (!token) {
    const isIOSStandalone = typeof window !== "undefined" && (window.navigator.standalone || window.matchMedia("(display-mode: standalone)").matches);
    const isIOSSafari = typeof window !== "undefined" && /iPhone|iPad|iPod/i.test(window.navigator.userAgent) && !window.navigator.standalone;
    return (
      <div className="min-h-screen bg-gradient-to-b from-forest-800 to-forest-900 text-white flex flex-col items-center justify-center p-6" data-testid="field-login">
        <Toaster position="top-center" toastOptions={toastOpts} />
        {isIOSSafari && !isIOSStandalone && (
          <div className="absolute top-4 left-4 right-4 bg-white/10 border border-white/20 rounded-2xl px-3 py-2 text-[11px] text-forest-100 text-center" data-testid="install-hint">
            Tip: tap <strong className="font-semibold">Share</strong> → <strong className="font-semibold">Add to Home Screen</strong> to install GOW Field as an app.
          </div>
        )}
        <div className="text-center mb-6">
          <ShieldCheck className="w-14 h-14 mx-auto text-forest-200 mb-3" />
          <h1 className="text-2xl font-bold">Georgia Outdoor Worx</h1>
          <p className="text-forest-200 text-sm mt-1">Field App · Enter your PIN</p>
        </div>
        <div className="flex items-center gap-3 mb-6" data-testid="pin-dots">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={`w-4 h-4 rounded-full border-2 transition-all ${pin.length > i ? "bg-white border-white" : "border-forest-400"}`} />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3 w-full max-w-xs" data-testid="numpad">
          {["1","2","3","4","5","6","7","8","9"].map(d => (
            <button key={d} onClick={() => handlePinDigit(d)} disabled={busy} data-testid={`pin-${d}`} className="w-20 h-20 mx-auto rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 text-3xl font-bold disabled:opacity-50">
              {d}
            </button>
          ))}
          <button onClick={handlePinClear} disabled={busy || !pin} data-testid="pin-clear" className="w-20 h-20 mx-auto rounded-full text-sm font-semibold text-forest-200 hover:bg-white/10 disabled:opacity-30">Clear</button>
          <button onClick={() => handlePinDigit("0")} disabled={busy} data-testid="pin-0" className="w-20 h-20 mx-auto rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 text-3xl font-bold disabled:opacity-50">0</button>
          <button onClick={handlePinDel} disabled={busy || !pin} data-testid="pin-del" className="w-20 h-20 mx-auto rounded-full text-sm font-semibold text-forest-200 hover:bg-white/10 disabled:opacity-30 flex items-center justify-center">
            <Delete className="w-6 h-6" />
          </button>
        </div>
        {busy && <Loader2 className="w-6 h-6 animate-spin mt-6 text-forest-200" />}
        <p className="text-[11px] text-forest-300 mt-8 text-center max-w-xs">Ask your manager to set your 4-digit PIN if you don't have one.</p>
      </div>
    );
  }

  // ====================== Ticket detail ======================
  if (selectedTicketId) {
    const fromHistory = view === "history";
    return (
      <TicketDetail
        ticketId={selectedTicketId}
        token={token}
        onBack={() => {
          setSelectedTicketId(null);
          if (fromHistory) fetchHistory(); else fetchTickets();
        }}
        onJobFinished={() => { setSelectedTicketId(null); fetchTickets(); toast.success("Job marked as completed."); }}
        tickRef={tick}
        liveSignal={liveSignal}
        pastMode={fromHistory}
      />
    );
  }

  // ====================== Home / History (operations) ======================
  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="field-home" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 88px)" }}>
      <Toaster position="top-center" toastOptions={toastOpts} />

      {view === "today" ? (
        <>
          {/* Top bar */}
          <header className="bg-forest-700 text-white px-5 pt-5 pb-6 rounded-b-3xl shadow-md">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-xs text-forest-200 uppercase tracking-wide font-semibold">Welcome back</p>
                <h1 className="text-xl font-bold mt-0.5" data-testid="field-user-name">{user?.name || "—"}</h1>
              </div>
              <button onClick={handleLogout} data-testid="field-logout-btn" className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 active:bg-white/30 px-3 py-2 rounded-full text-xs font-semibold min-h-[44px]">
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
            <p className="text-xs text-forest-200">{fmtDateLabel(new Date().toISOString())}</p>
          </header>

          {/* Current shift */}
          <div className="px-4 mt-4">
            {loading ? (
              <div className="bg-white rounded-2xl border border-stone-200 p-6 text-center text-sm text-stone-400">Loading...</div>
            ) : openShift ? (
              <div className="bg-white rounded-2xl border-2 border-amber-300 p-4 shadow-sm" data-testid="active-shift-card">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-[10px] uppercase tracking-wide font-bold text-amber-600 flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" /> Active shift
                    </span>
                    <p className="text-xs text-stone-500 mt-2">Started at <strong className="text-stone-700">{fmtTime(openShift.clock_in)}</strong></p>
                    <p className="text-sm text-amber-700 font-semibold mt-0.5" data-testid="active-shift-elapsed">Elapsed: {elapsed(openShift.clock_in)}<span className="hidden">{tick}</span></p>
                  </div>
                  <button
                    data-testid="clock-out-btn"
                    onClick={handleClockOut}
                    disabled={busy}
                    className="bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-bold py-3 px-5 rounded-2xl shadow flex items-center justify-center gap-2 disabled:opacity-50 min-h-[56px]"
                  >
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Square className="w-5 h-5" />}
                    Clock Out
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm text-center" data-testid="no-shift-card">
                <p className="text-xs text-stone-500 mb-3">No active shift</p>
                <button
                  data-testid="clock-in-btn"
                  onClick={handleClockIn}
                  disabled={busy}
                  className="w-full bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-bold py-5 rounded-2xl shadow-lg flex items-center justify-center gap-2 text-lg disabled:opacity-50 min-h-[64px]"
                >
                  {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Clock className="w-6 h-6" />}
                  Clock In
                </button>
              </div>
            )}
          </div>

          {/* Tickets */}
          <div className="px-4 mt-5">
            <div className="flex items-center justify-between mb-2 px-1">
              <h2 className="text-sm font-semibold text-stone-800 flex items-center gap-1.5">
                <Wrench className="w-4 h-4 text-forest-700" /> Today's Tickets
              </h2>
              <span className="text-xs text-stone-500">{tickets.filter(t => t.status !== "completed").length} pending</span>
            </div>
            {tickets.length === 0 ? (
              <div className="bg-white rounded-2xl border border-stone-200 p-6 text-center text-xs text-stone-400" data-testid="no-tickets">
                No tickets assigned for today
              </div>
            ) : (
              <div className="space-y-2.5" data-testid="ticket-list">
                {tickets.map(t => (
                  <TicketCard key={t.id} t={t} onOpen={() => setSelectedTicketId(t.id)} />
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <HistoryView
          data={pastTickets}
          loading={loadingHistory}
          onOpenTicket={(id) => setSelectedTicketId(id)}
          onRefresh={fetchHistory}
        />
      )}

      {/* Bottom navigation (Instagram-style) */}
      <BottomNav
        view={view}
        onBackPress={() => {
          if (view === "history") setView("today");
          else handleLogout();
        }}
        onHistoryPress={() => setView(view === "history" ? "today" : "history")}
      />
    </div>
  );
}

function BottomNav({ view, onBackPress, onHistoryPress }) {
  const backLabel = view === "history" ? "Today" : "Sign out";
  return (
    <nav
      data-testid="field-bottom-nav"
      className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-stone-200 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="flex items-stretch justify-around max-w-md mx-auto">
        <button
          data-testid="nav-back-btn"
          onClick={onBackPress}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 py-3 min-h-[64px] text-stone-700 active:bg-stone-50"
        >
          <ArrowLeft className="w-6 h-6" />
          <span className="text-[11px] font-semibold">{backLabel}</span>
        </button>
        <div className="w-px bg-stone-200" />
        <button
          data-testid="nav-history-btn"
          onClick={onHistoryPress}
          className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-3 min-h-[64px] active:bg-stone-50 ${
            view === "history" ? "text-forest-700" : "text-stone-700"
          }`}
        >
          <History className="w-6 h-6" />
          <span className="text-[11px] font-semibold">Past Tickets</span>
        </button>
      </div>
    </nav>
  );
}

function HistoryView({ data, loading, onOpenTicket, onRefresh }) {
  const groups = data?.groups || [];
  return (
    <div data-testid="field-history-view">
      <header className="bg-forest-700 text-white px-5 pt-5 pb-6 rounded-b-3xl shadow-md">
        <div className="flex items-center justify-between mb-1">
          <div>
            <p className="text-xs text-forest-200 uppercase tracking-wide font-semibold">Past Tickets</p>
            <h1 className="text-xl font-bold mt-0.5">Last 14 days</h1>
          </div>
          <button
            onClick={onRefresh}
            data-testid="history-refresh-btn"
            className="bg-white/10 hover:bg-white/20 active:bg-white/30 px-3 py-2 rounded-full text-xs font-semibold min-h-[44px]"
          >
            Refresh
          </button>
        </div>
        <p className="text-xs text-forest-200 mt-1">Upload photos to any past job. Start/Finish actions are disabled here.</p>
      </header>

      <div className="px-4 mt-5">
        {loading ? (
          <div className="bg-white rounded-2xl border border-stone-200 p-8 text-center text-sm text-stone-400">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading history…
          </div>
        ) : groups.length === 0 ? (
          <div className="bg-white rounded-2xl border border-stone-200 p-8 text-center text-xs text-stone-400" data-testid="no-history">
            No past tickets in the last 14 days.
          </div>
        ) : (
          groups.map((g) => (
            <div key={g.date} className="mb-5" data-testid={`history-day-${g.date}`}>
              <div className="text-[11px] font-bold uppercase tracking-wide text-stone-500 mb-2 px-1">
                {fmtDateLabel(`${g.date}T12:00:00`)}
                <span className="ml-2 text-stone-400 font-medium normal-case">· {g.tickets.length} ticket{g.tickets.length !== 1 ? "s" : ""}</span>
              </div>
              <div className="space-y-2.5">
                {g.tickets.map((t) => (
                  <TicketCard key={t.id} t={t} onOpen={() => onOpenTicket(t.id)} />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function TicketCard({ t, onOpen }) {
  const isCompleted = t.status === "completed";
  const isInProgress = t.status === "in_progress";
  // "Late" = original end_time slot has already passed but the job is not yet started.
  // We still show the card (regulatory: never hide an unfinished ticket); just nudge
  // the worker visually with an amber border.
  const isLate = (() => {
    if (isCompleted || isInProgress) return false;
    if (!t.scheduled_date) return false;
    const end = t.end_time || t.start_time;
    if (!end) return false;
    const [hh, mm] = String(end).split(":").map(Number);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return false;
    const [y, m, d] = String(t.scheduled_date).split("-").map(Number);
    const endLocal = new Date(y, (m || 1) - 1, d || 1, hh, mm, 0);
    return Date.now() > endLocal.getTime();
  })();

  const statusColor = isCompleted ? "bg-stone-100 text-stone-500"
    : isInProgress ? "bg-amber-100 text-amber-700"
    : "bg-forest-100 text-forest-700";
  const statusLabel = isCompleted ? "Completed"
    : isInProgress ? "In progress"
    : isLate ? "Overdue"
    : "Scheduled";
  const lateBorder = isLate ? "border-amber-400 ring-1 ring-amber-200" : "border-stone-200";

  return (
    <button
      onClick={onOpen}
      data-testid={`ticket-card-${t.id}`}
      data-late={isLate ? "1" : "0"}
      className={`w-full bg-white rounded-2xl border p-4 shadow-sm flex items-start gap-3 text-left active:scale-[0.99] transition-transform ${isCompleted ? "border-stone-200 opacity-60" : lateBorder}`}
    >
      <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${isLate ? "bg-amber-100 text-amber-700" : statusColor}`}>
        <Wrench className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <span className="text-xs font-bold text-stone-500" data-testid={`ticket-number-${t.id}`}>{t.ticket_number}</span>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${isLate ? "bg-amber-100 text-amber-700" : statusColor}`}>{statusLabel}</span>
        </div>
        <p className="text-sm font-bold text-stone-900 truncate">{t.client_name}</p>
        {t.primary_service && <p className="text-xs text-stone-600 truncate">{t.primary_service}{t.services_count > 1 ? ` + ${t.services_count - 1} more` : ""}</p>}
        <div className="flex items-center gap-3 mt-1.5 text-[11px] text-stone-500">
          {t.start_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t.start_time}</span>}
          {t.address && <span className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3" /> {t.address.split(",")[0]}</span>}
        </div>
      </div>
    </button>
  );
}

function TicketDetail({ ticketId, token, onBack, onJobFinished, tickRef, liveSignal, pastMode = false }) {
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [facadeUrl, setFacadeUrl] = useState(null);
  const [completionUrls, setCompletionUrls] = useState({});
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTicket = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/field/tickets/${ticketId}`, { headers });
      setT(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load ticket"); onBack(); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  useEffect(() => { fetchTicket(); }, [fetchTicket]);

  // Re-fetch silently when a real-time event arrives (no loading flicker)
  const initialSignalRef = useRef(liveSignal);
  useEffect(() => {
    if (liveSignal === initialSignalRef.current) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/field/tickets/${ticketId}`, { headers });
        setT(data);
      } catch (err) { console.warn("[field] silent refetch failed:", err?.message); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveSignal, ticketId]);

  // Lazy-load facade as blob
  useEffect(() => {
    if (!t?.facade_photo_path) { setFacadeUrl(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API}/field/files/${t.facade_photo_path}`, { headers, responseType: "blob" });
        if (!cancelled) setFacadeUrl(URL.createObjectURL(resp.data));
      } catch (err) { console.warn("[field] facade load failed:", err?.message); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t?.facade_photo_path]);

  // Lazy-load completion thumbnails
  useEffect(() => {
    if (!t?.completion_photos?.length) return;
    let cancelled = false;
    t.completion_photos.forEach(async (p) => {
      if (completionUrls[p.id]) return;
      try {
        const resp = await axios.get(`${API}/field/files/${p.storage_path}`, { headers, responseType: "blob" });
        if (!cancelled) setCompletionUrls(prev => ({ ...prev, [p.id]: URL.createObjectURL(resp.data) }));
      } catch (err) { console.warn("[field] photo load failed:", err?.message); }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t?.completion_photos]);

  const handleStart = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/field/tickets/${ticketId}/start`, {}, { headers });
      setT(prev => ({ ...prev, status: "in_progress", actual_start_time: data.actual_start_time || prev?.actual_start_time }));
      toast.success("Job started");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const handleFinish = async () => {
    if (!window.confirm("Mark this job as completed?")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/field/tickets/${ticketId}/finish`, {}, { headers });
      onJobFinished();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const handleUploadPhotos = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    let uploaded = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(`${API}/field/tickets/${ticketId}/photos`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
        uploaded++;
      } catch (err) { console.warn("[field] upload failed:", err?.message); }
    }
    if (uploaded > 0) toast.success(`${uploaded} photo${uploaded !== 1 ? "s" : ""} uploaded`);
    if (uploaded < files.length) toast.error(`${files.length - uploaded} failed`);
    setBusy(false);
    fetchTicket();
  };

  const openMaps = () => {
    if (!t?.address) return;
    const ua = navigator.userAgent || "";
    const isApple = /iPhone|iPad|iPod|Mac/i.test(ua);
    const q = encodeURIComponent(t.address);
    const url = isApple ? `https://maps.apple.com/?daddr=${q}` : `https://www.google.com/maps/dir/?api=1&destination=${q}`;
    window.open(url, "_blank");
  };

  if (loading || !t) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="w-7 h-7 animate-spin text-forest-700" />
      </div>
    );
  }

  const isScheduled = t.status === "scheduled";
  const isInProgress = t.status === "in_progress";
  const isCompleted = t.status === "completed";

  return (
    <div className="min-h-screen bg-stone-50 pb-32" data-testid="ticket-detail" style={{ paddingBottom: "max(env(safe-area-inset-bottom), 6rem)" }}>
      <Toaster position="top-center" toastOptions={toastOpts} />

      {/* Header */}
      <header className="bg-forest-700 text-white px-5 pt-4 pb-5 rounded-b-3xl shadow-md">
        <button onClick={onBack} className="inline-flex items-center gap-1 text-forest-200 text-sm mb-2 min-h-[44px]" data-testid="ticket-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h1 className="text-2xl font-bold" data-testid="ticket-title">Ticket {t.ticket_number}</h1>
        {/* Time card */}
        {(t.start_time || t.end_time) && (
          <div className="mt-3 bg-forest-600/40 rounded-xl px-3 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-forest-500/30 flex items-center justify-center"><Clock className="w-4 h-4" /></div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-forest-200">Scheduled</p>
                <p className="text-sm font-bold">{t.start_time}{t.end_time ? ` – ${t.end_time}` : ""}</p>
              </div>
            </div>
            <span className="bg-white text-forest-800 text-xs font-bold px-2.5 py-1 rounded-full">{t.start_time || "TBD"}</span>
          </div>
        )}
      </header>

      <div className="px-4 mt-4 space-y-3">
        {/* Customer */}
        <DetailRow icon={<UserIcon className="w-5 h-5 text-forest-700" />} label="Customer">
          <p className="text-base font-bold text-stone-900">{t.client_name || "—"}</p>
          {t.client_phone && (
            <a href={`tel:${t.client_phone.replace(/\s/g, "")}`} className="text-sm font-semibold text-forest-700 hover:text-forest-800 inline-flex items-center gap-1 mt-0.5">
              <Phone className="w-3.5 h-3.5" /> {t.client_phone}
            </a>
          )}
        </DetailRow>

        {/* Address */}
        {t.address && (
          <DetailRow icon={<MapPin className="w-5 h-5 text-forest-700" />} label="Address" right={
            <button onClick={openMaps} data-testid="ticket-navigate" className="w-11 h-11 rounded-full bg-forest-50 hover:bg-forest-100 active:bg-forest-200 flex items-center justify-center text-forest-700 min-h-[44px] min-w-[44px]" title="Navigate">
              <Navigation2 className="w-5 h-5" />
            </button>
          }>
            <p className="text-base font-bold text-stone-900 leading-tight">{t.address}</p>
          </DetailRow>
        )}

        {/* Service */}
        <DetailRow icon={<Wrench className="w-5 h-5 text-forest-700" />} label="Service type">
          <p className="text-base font-bold text-stone-900">{t.primary_service || "—"}</p>
          {t.services?.length > 1 && <p className="text-xs text-stone-500 mt-0.5">+ {t.services.length - 1} more service{t.services.length - 1 !== 1 ? "s" : ""}</p>}
          {t.notes && <p className="text-xs text-stone-500 mt-1.5 italic">{t.notes}</p>}
        </DetailRow>

        {/* Facade photo */}
        <DetailRow icon={<ImageIcon className="w-5 h-5 text-forest-700" />} label="Facade photo" subtitle="Confirm you're at the right property">
          {facadeUrl ? (
            <img src={facadeUrl} alt="Property facade" className="w-full max-h-56 object-cover rounded-xl mt-1" data-testid="ticket-facade" />
          ) : t.facade_photo_path ? (
            <div className="w-full h-32 rounded-xl bg-stone-100 flex items-center justify-center text-stone-400 text-xs">Loading photo…</div>
          ) : (
            <p className="text-xs text-stone-400 italic mt-1">No facade photo uploaded yet</p>
          )}
        </DetailRow>

        {/* Job status */}
        <div className="bg-white rounded-2xl border border-stone-200 p-4 shadow-sm">
          <p className="text-xs uppercase font-semibold tracking-wide text-stone-500">Job status</p>
          <div className="flex items-center justify-between mt-1">
            <div>
              <p className={`text-base font-bold ${isCompleted ? "text-stone-500" : isInProgress ? "text-amber-700" : "text-forest-700"}`} data-testid="ticket-status">
                <span className={`inline-block w-2 h-2 rounded-full mr-2 ${isCompleted ? "bg-stone-400" : isInProgress ? "bg-amber-500" : "bg-forest-500"}`} />
                {isCompleted ? "Completed" : isInProgress ? "In progress" : "Scheduled"}
              </p>
              {(isInProgress || isCompleted) && t.actual_start_time && (
                <p className="text-xs text-stone-500 mt-0.5">
                  Started at {fmtTime(t.actual_start_time)}{isInProgress && <> · <span className="font-semibold text-amber-700" data-testid="ticket-elapsed">{elapsedHMS(t.actual_start_time)}<span className="hidden">{tickRef}</span></span></>}
                </p>
              )}
            </div>
            <label className="bg-forest-50 hover:bg-forest-100 active:bg-forest-200 text-forest-800 px-3 py-2 rounded-xl text-xs font-bold cursor-pointer flex items-center gap-1.5 min-h-[44px]" data-testid="ticket-upload-photos-btn">
              <Camera className="w-4 h-4" /> Add photos
              <input type="file" accept="image/*" multiple className="hidden" onChange={e => handleUploadPhotos(Array.from(e.target.files || []))} />
            </label>
          </div>
          {t.completion_photos?.length > 0 && (
            <div className="mt-3" data-testid="completion-photos-grid">
              <p className="text-[10px] uppercase tracking-wide font-semibold text-stone-500 mb-1.5">Proof of service ({t.completion_photos.length})</p>
              <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1">
                {t.completion_photos.map(p => (
                  <div key={p.id} className="w-16 h-16 rounded-lg bg-stone-100 flex-shrink-0 overflow-hidden">
                    {completionUrls[p.id] ? <img src={completionUrls[p.id]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-4 h-4 text-stone-300" /></div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom action bar — hidden in pastMode (past-tickets view: photo uploads only) */}
      {!isCompleted && !pastMode && (
        <div className="fixed left-0 right-0 bottom-0 bg-white border-t border-stone-200 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] p-3" style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.75rem)" }} data-testid="ticket-action-bar">
          <div className="max-w-md mx-auto flex gap-2">
            {isScheduled && (
              <button
                onClick={handleStart}
                disabled={busy}
                data-testid="ticket-start-btn"
                className="flex-1 bg-forest-700 hover:bg-forest-800 active:bg-forest-900 text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-50 min-h-[56px]"
              >
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />} Start
              </button>
            )}
            {isInProgress && (
              <button
                onClick={handleFinish}
                disabled={busy}
                data-testid="ticket-finish-btn"
                className="flex-1 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-50 min-h-[56px]"
              >
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />} Finish
              </button>
            )}
          </div>
        </div>
      )}
      {pastMode && (
        <div className="fixed left-0 right-0 bottom-0 bg-amber-50 border-t border-amber-200 p-3 text-center text-xs text-amber-900 font-semibold" style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.75rem)" }} data-testid="past-mode-banner">
          Past job view · photo upload only
        </div>
      )}
    </div>
  );
}

function DetailRow({ icon, label, subtitle, children, right }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-4 shadow-sm flex items-start gap-3">
      <div className="w-10 h-10 rounded-full bg-forest-50 flex items-center justify-center flex-shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-wide font-semibold text-stone-500">{label}</p>
        {subtitle && <p className="text-[11px] text-stone-400 mt-0.5 italic">{subtitle}</p>}
        <div className="mt-1">{children}</div>
      </div>
      {right}
    </div>
  );
}

export default function FieldApp() {
  return (
    <ErrorBoundary scope="the field app">
      <FieldAppInner />
    </ErrorBoundary>
  );
}

